/**
 * Generic CRUD utility over sessionStorage for Playground Mode.
 * Each entity type gets its own sessionStorage key (e.g. 'pg_remote_configs').
 * Enforces a configurable max-items limit per entity.
 */
export class PlaygroundSessionStore {
    constructor(entityKey, maxItems = 5) {
        this.entityKey = entityKey;
        this.maxItems = maxItems;
    }

    _read() {
        try {
            const raw = sessionStorage.getItem(this.entityKey);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    }

    _write(data) {
        sessionStorage.setItem(this.entityKey, JSON.stringify(data));
    }

    getAll() {
        return this._read();
    }

    getById(id) {
        const items = this._read();
        return items.find(item => String(item.id) === String(id)) || null;
    }

    create(data) {
        const items = this._read();
        if (items.length >= this.maxItems) {
            throw new PlaygroundLimitError(
                `Playground limit reached: maximum ${this.maxItems} ${this.entityKey.replace('pg_', '').replace(/_/g, ' ')} allowed.`
            );
        }
        const id = data.id || PlaygroundSessionStore.getNextId();
        const newItem = { ...data, id };
        items.push(newItem);
        this._write(items);
        return newItem;
    }

    update(id, data) {
        const items = this._read();
        const index = items.findIndex(item => String(item.id) === String(id));
        if (index === -1) {
            throw new Error(`Item with id ${id} not found in ${this.entityKey}`);
        }
        items[index] = { ...items[index], ...data, id };
        this._write(items);
        return items[index];
    }

    delete(id) {
        const items = this._read();
        const filtered = items.filter(item => String(item.id) !== String(id));
        this._write(filtered);
        return true;
    }

    static getNextId() {
        const counter = parseInt(sessionStorage.getItem('pg_id_counter') || '100', 10) + 1;
        sessionStorage.setItem('pg_id_counter', String(counter));
        return `pg-${counter}`;
    }
}

/**
 * Custom error thrown when the playground item limit is exceeded.
 */
export class PlaygroundLimitError extends Error {
    constructor(message) {
        super(message);
        this.name = 'PlaygroundLimitError';
    }
}
